<script lang="ts">
	// T22: the till's order store is starting, or restarting, inside the Bosla
	// app (#392). The shell starts it with the app and again whenever it stops;
	// until it answers, the till takes no sale. Built like the suspended cover
	// (T13): it covers the whole till and nothing beneath it can be tapped,
	// but nothing beneath it unmounts either, so the cart is where it was when
	// the store answers again and the cover goes. Every sale the store
	// confirmed is in its database, so none is lost meanwhile.
	//
	// It clears on its own; there is nothing to press. After a minute it adds
	// what to try, because a store that never comes back needs a person.

	import Loading from '../components/Loading.svelte';
	import { message, type Language } from '../i18n/messages';
	import type { StoreStatus } from '../till/shell';

	interface Props {
		language: Language;
		status: Exclude<StoreStatus, 'up'>;
	}

	let { language, status }: Props = $props();

	/** How long the cover waits before it says what to try. */
	const STILL_DOWN_MS = 60_000;

	let long = $state(false);
	$effect(() => {
		const timer = setTimeout(() => (long = true), STILL_DOWN_MS);
		return () => clearTimeout(timer);
	});

	let cover: HTMLDivElement | undefined = $state();
	$effect(() => {
		cover?.focus();
	});
</script>

<div
	class="cover"
	role="alertdialog"
	aria-modal="true"
	aria-live="polite"
	aria-label={message(language, 'storeStartingLabel')}
	tabindex="-1"
	bind:this={cover}
	data-testid="store-down"
	data-status={status}
>
	<div class="body">
		<Loading size="lg" label={message(language, 'storeStartingLabel')} />
		<div class="head">
			<h1>
				{message(language, status === 'down' ? 'storeDownTitle' : 'storeStartingTitle')}
			</h1>
			<p class="tell">
				{message(language, status === 'down' ? 'storeDownTell' : 'storeStartingTell')}
			</p>
		</div>
		{#if long}
			<p class="still" data-testid="store-down-still">{message(language, 'storeStillDown')}</p>
		{/if}
	</div>
</div>

<style>
	.cover {
		outline: none;
		position: fixed;
		inset: 0;
		z-index: var(--z-cover);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--space-9);
		background: var(--surface-page);
		color: var(--text-strong);
		overflow-y: auto;
	}

	.body {
		inline-size: 100%;
		max-inline-size: 560px;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.head {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	h1 {
		margin: 0;
		font-size: var(--text-h3);
		font-weight: 700;
		letter-spacing: var(--tracking-tight);
		line-height: var(--leading-headline);
		text-wrap: pretty;
	}

	.tell {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 600;
		color: var(--text-strong);
		text-wrap: pretty;
	}

	.still {
		margin: 0;
		max-inline-size: var(--measure);
		font-size: var(--text-base);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}
</style>
